import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import TradingDashboard from "./components/tradingDashboard";
import LoginPage from "./components/login";
import BannedPage from "./components/BannedPage";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "./firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { NotificationsProvider } from "./hooks/notificationsContext";
import useServerClock from "./hooks/useServerClock";
import calculatePortfolio from './utils/calculatePortfolio';
import ReferralErrorPage from "./components/ReferralErrorPage";

function ReferralRedirect() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  useEffect(() => {
    if (code) {
      localStorage.setItem("pendingReferralCode", code);
    }
  }, [code]);

  return <Navigate to="/login" replace />;
}

export default function App() {
  const [currentUser, loading] = useAuthState(auth);
  const [activeVote, setActiveVote] = useState(null);
  const [isBanned, setIsBanned] = useState(false);
  const [isProcessingReferral, setIsProcessingReferral] = useState(false);

  useServerClock(!loading && !!currentUser);

  useEffect(() => {
    const voteRef = doc(db, "globalEvents", "current_vote");
    const unsubVote = onSnapshot(voteRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setActiveVote(data.status === "active" ? { id: snap.id, ...data } : null);
      } else {
        setActiveVote(null);
      }
    }, (err) => console.error("Vote Listener Error:", err));
    return () => unsubVote();
  }, []);

  useEffect(() => {
    if (loading || !currentUser) {
      setIsBanned(false);
      return;
    }

    let announced = false;
    const playerRef = doc(db, "Users", currentUser.uid);

    const unsub = onSnapshot(playerRef, async (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();

      if (data.userBanned === true) {
        setIsBanned(true);
        auth.signOut();
        return; 
      }

      if (data.userAnnounced !== true && !announced) {
        announced = true;
        try {
          const idToken = await currentUser.getIdToken();
          await fetch("https://announcenewplayer-5dhfwyjvzq-uc.a.run.app", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ UID: currentUser.uid }),
          });
        } catch (err) {
          console.error("Error announcing player:", err);
        }
      }
    });

    return () => unsub();
  }, [loading, currentUser]);

  useEffect(() => {
  const processReferral = async () => {
    const savedCode = localStorage.getItem("pendingReferralCode");

    if (currentUser && savedCode && !isProcessingReferral) {
      setIsProcessingReferral(true);
      
      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch("https://referral-5dhfwyjvzq-uc.a.run.app", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
          },
          body: JSON.stringify({ referralCode: savedCode.trim() })
        });

        localStorage.removeItem("pendingReferralCode");

if (response.ok) {
    window.location.href = "https://outtradeonline.com/referral-activated.html";
    return; 
} else {
    const errorData = await response.json();
    window.location.href = `/referral-error?reason=${errorData.error || 'invalid'}`;
    return;
}
      } catch (err) {
        console.error("Referral activation failed:", err);
        localStorage.removeItem("pendingReferralCode");

        setIsProcessingReferral(false);
      }
    }
  };
  
  if (!loading && currentUser) {
    processReferral();
  }
}, [currentUser, loading, isProcessingReferral]);

if (loading || isProcessingReferral) {
  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: 'column',
      height: '100vh', 
      alignItems: 'center', 
      justifyContent: 'center', 
      backgroundColor: '#050505',
      color: '#50e3c2',
      fontFamily: "'JetBrains Mono', monospace",
      textAlign: 'center',
      textShadow: '0 0 10px rgba(80, 227, 194, 0.3)'
    }}>

      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(80, 227, 194, 0.1)',
        borderTop: '3px solid #50e3c2',
        borderRadius: '50%',
        marginBottom: '20px',
        animation: 'spin 1s linear infinite'
      }} />

      <h2 style={{ 
        letterSpacing: '2px', 
        fontSize: '1.2rem',
        textTransform: 'uppercase',
        margin: 0
      }}>
        {isProcessingReferral ? "LOADING REFERRAL..." : "INITIALIZING..."}
      </h2>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

  return (
    <NotificationsProvider>
      <Router>
        <Routes>
          <Route path="/referral-error" element={<ReferralErrorPage />} />
          <Route path="/referral" element={<ReferralRedirect />} />

          <Route
            path="/"
            element={
              isBanned ? (
                <Navigate to="/banned" replace />
              ) : currentUser ? (
                <TradingDashboard
                  currentUser={currentUser}
                  activeVote={activeVote} 
                  onLogout={() => auth.signOut()}
                  calculatePortfolio={calculatePortfolio}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          
          <Route 
            path="/login" 
            element={
              (() => {
                const hasPending = localStorage.getItem("pendingReferralCode");
                if (currentUser && !hasPending) {
                  return <Navigate to="/" replace />;
                }
                return <LoginPage />;
              })()
            } 
          />

          <Route path="/banned" element={<BannedPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </NotificationsProvider>
  );
}